import React from 'react';
import { Box } from '@mui/material';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register the required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * TestGrowthChart component for displaying test case growth trends with automation status stacking
 * @param {Object} props - Component props
 * @param {Array} props.growthData - Array of growth data objects with automation breakdown
 * @param {Array} props.automationStatuses - Array of automation status names for stacking
 * @param {number} props.baselineCount - Number of tests that existed before the chart timeline
 * @returns {JSX.Element} TestGrowthChart component
 */
const TestGrowthChart = ({ growthData = [], automationStatuses = [], baselineCount = 0 }) => {
  // Extract labels and data from growthData
  const labels = growthData.map(item => item.monthName);
  
  // Define colors for simplified automation statuses
  const statusColors = {
    'Automated': 'rgba(0, 176, 80, 0.7)',     // Green
    'Manual': 'rgba(205, 92, 92, 0.7)',       // Indian Red
    'Requires Automation': 'rgba(47, 79, 79, 0.7)' // Dark Slate Gray
  };

  // Create datasets for stacked bars (one for each automation status)
  const barDatasets = automationStatuses.map(status => ({
    type: 'bar',
    label: status,
    data: growthData.map(item => item.automationBreakdown[status] || 0),
    backgroundColor: statusColors[status] || 'rgba(96, 125, 139, 0.7)',
    borderColor: statusColors[status]?.replace('0.7', '1') || 'rgba(96, 125, 139, 1)',
    borderWidth: 1,
    yAxisID: 'y',
    stack: 'monthly'
  }));

  // Calculate cumulative totals for the line chart, starting with baseline count
  const cumulativeCounts = [];
  let runningTotal = baselineCount;
  growthData.forEach(item => {
    runningTotal += item.totalCount;
    cumulativeCounts.push(runningTotal);
  });

  // Chart data with mixed chart types
  const chartData = {
    labels,
    datasets: [
      ...barDatasets,
      {
        type: 'line',
        label: 'Cumulative Total',
        data: cumulativeCounts,
        borderColor: '#ff6b35',
        backgroundColor: 'rgba(255, 107, 53, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#ff6b35',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        yAxisID: 'y1',
      }
    ],
  };

  // Chart options with dual y-axes
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Test Cases Growth - Monthly Creation vs Cumulative Total',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 15,
          padding: 15,
          usePointStyle: true,
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#2196f3',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label;
            const value = context.raw;
            if (label === 'Cumulative Total') {
              return `${label}: ${value} total test cases`;
            } else {
              return `${label}: ${value} test cases`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Month',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Monthly Test Cases Created',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#2196f3'
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          },
          color: '#2196f3'
        },
        grid: {
          drawOnChartArea: false
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cumulative Total',
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#ff6b35'
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          },
          color: '#ff6b35'
        },
        grid: {
          drawOnChartArea: true
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  // If no data, display a message
  if (growthData.length === 0) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        minHeight="300px"
      >
        No growth data available
      </Box>
    );
  }

  return (
    <Box height="400px" width="100%" overflow="hidden">
      <Chart type="bar" data={chartData} options={options} />
    </Box>
  );
};

export default TestGrowthChart;
